
import React, { useState, useMemo } from 'react';
import { Attendee } from '@/types/attendee';
import AttendeeCard from './AttendeeCard';
import { ScrollArea } from '@/components/ui/scroll-area';
import { filterAttendees } from '../tableUtils';
import PaymentConfirmDialog from '../payment/PaymentConfirmDialog';
import { motion } from 'framer-motion';

interface MobileTableBodyProps {
  attendees: Attendee[];
  searchQuery: string;
  onCheckInSuccess: (attendee: Attendee, timestamp: string) => void;
  onDirectUpdate?: (code: string, updates: any) => Promise<any>;
  onSilentUpdate?: (attendee: Attendee) => void;
  showContribution: boolean;
  height?: string;
}

const MobileTableBody: React.FC<MobileTableBodyProps> = ({
  attendees,
  searchQuery,
  onCheckInSuccess,
  onDirectUpdate,
  onSilentUpdate,
  showContribution,
  height = 'calc(100vh - 270px)'
}) => {
  // State for expanded rows outside of the render loop
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  
  // State for payment confirmation dialog
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState<Attendee | null>(null);
  const [pendingPaymentStatus, setPendingPaymentStatus] = useState<boolean | null>(null);
  
  // Filter attendees based on search query
  const filteredAttendees = useMemo(() => {
    return filterAttendees(attendees, searchQuery);
  }, [attendees, searchQuery]);
  
  // Render "no results" state when filtered list is empty
  const renderEmptyState = () => (
    <motion.div 
      className="flex flex-col items-center justify-center p-8 text-center"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="text-5xl text-slate-300 dark:text-slate-600 mb-4">🔍</div>
      <h3 className="font-medium text-lg text-slate-500 dark:text-slate-400">Không tìm thấy kết quả</h3>
      <p className="text-sm text-slate-400 dark:text-slate-500 mt-2 max-w-xs">
        Hãy thử tìm kiếm với từ khóa khác hoặc kiểm tra lại chính tả
      </p>
    </motion.div>
  );
  
  // Toggle row expansion
  const toggleRowExpansion = (code: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [code]: !prev[code]
    }));
  };
  
  // Handle payment toggle request
  const handlePaymentToggleRequest = (attendee: Attendee, checked: boolean) => {
    setSelectedAttendee(attendee);
    setPendingPaymentStatus(checked);
    setShowPaymentDialog(true);
  };
  
  // Handle payment confirmation
  const handlePaymentConfirm = async () => {
    if (!selectedAttendee || pendingPaymentStatus === null || !onDirectUpdate) return;
    
    try {
      // Update with direct update function, preserving t_shirt_size
      await onDirectUpdate(selectedAttendee.code, {
        paid: pendingPaymentStatus,
        t_shirt_size: selectedAttendee.t_shirt_size
      });
      
      setShowPaymentDialog(false);
      setPendingPaymentStatus(null);
      setSelectedAttendee(null);
    } catch (error) {
      console.error('Error confirming payment:', error);
    }
  };
  
  // Handle cancel
  const handlePaymentCancel = () => {
    setShowPaymentDialog(false);
    setPendingPaymentStatus(null);
    setSelectedAttendee(null);
  };

  // Enhanced direct update function that ensures t_shirt_size is preserved
  const enhancedDirectUpdate = async (code: string, updates: any) => {
    try {
      // Find the attendee to get t_shirt_size
      const currentAttendee = attendees.find(a => a.code === code);
      
      // If no attendee found, return early
      if (!currentAttendee) {
        console.error(`MobileTableBody: Cannot find attendee with code ${code}`);
        return null;
      }
      
      // Always include t_shirt_size in updates
      const enhancedUpdates = {
        ...updates,
        t_shirt_size: updates.t_shirt_size || currentAttendee.t_shirt_size
      };
      
      console.log(`MobileTableBody: Enhanced update for ${code} with t_shirt_size=${enhancedUpdates.t_shirt_size}`);
      
      // Call the original onDirectUpdate with preserved t_shirt_size
      if (onDirectUpdate) {
        const result = await onDirectUpdate(code, enhancedUpdates);
        
        // Further ensure t_shirt_size is preserved in the result
        if (result) {
          result.t_shirt_size = result.t_shirt_size || currentAttendee.t_shirt_size;
        }
        
        return result;
      }
      
      return null;
    } catch (error) {
      console.error('Error in enhancedDirectUpdate:', error);
      return null;
    }
  };
  
  // Create content element based on filtered attendees 
  const content = filteredAttendees.length === 0 ? (
    renderEmptyState()
  ) : (
    <motion.div 
      className="space-y-3 pb-3 pt-2 px-2"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, staggerChildren: 0.05 }}
    >
      {filteredAttendees.map((attendee, index) => (
        <motion.div
          key={attendee.code}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.03 }}
        >
          <AttendeeCard
            attendee={attendee}
            isPaid={attendee.paid}
            isUpdating={false}
            localAttendance={attendee.attended}
            localCheckedInAt={attendee.checkedInAt}
            onCheckInSuccess={(updatedAttendee, timestamp) => {
              console.log(`MobileTableBody: Check-in success with t_shirt_size=${updatedAttendee.t_shirt_size}`);
              onCheckInSuccess(updatedAttendee, timestamp);
            }}
            onDirectUpdate={enhancedDirectUpdate}
            onSilentUpdate={onSilentUpdate}
            handlePaymentToggleRequest={(checked) => {
              handlePaymentToggleRequest(attendee, checked);
            }}
            showContribution={showContribution}
            isExpanded={!!expandedRows[attendee.code]}
            onToggleExpand={() => toggleRowExpansion(attendee.code)}
          />
        </motion.div>
      ))}
    </motion.div>
  );
  
  return (
    <>
      <ScrollArea 
        className={`h-[${height}] overflow-y-auto rounded-md border border-gray-100 dark:border-gray-800 shadow-sm bg-white dark:bg-gray-900`}
      >
        {content}
      </ScrollArea>
      
      {/* Payment confirmation dialog with corrected props */}
      {selectedAttendee && (
        <PaymentConfirmDialog
          attendee={selectedAttendee}
          isOpen={showPaymentDialog}
          setIsOpen={setShowPaymentDialog}
          pendingStatus={pendingPaymentStatus}
          onConfirm={handlePaymentConfirm}
          onCancel={handlePaymentCancel}
        />
      )}
    </>
  );
};

export default MobileTableBody;
